import React from 'react';
import { FieldType, Language, ItemType } from '../../../types/pageBuilder';
import ImageUploader from './ImageUploader';
import ItemsList from './ItemsList';
import IconSelector from './IconSelector';
import StatisticsEditor from './StatisticsEditor';

interface FieldEditorProps {
  fieldKey: string;
  fieldValue: string | any[];
  fieldType: FieldType;
  label: string;
  onChange: (value: string | any[]) => void;
  // Additional props for new field types
  sectionId?: number;
  language?: Language;
  onItemsChange?: () => void;
}

/**
 * Dynamic field editor component (Phase 3 enhanced)
 * Renders appropriate input based on field type
 */
const FieldEditor: React.FC<FieldEditorProps> = ({
  fieldKey,
  fieldValue,
  fieldType,
  label,
  onChange,
  sectionId,
  language = 'en',
  onItemsChange
}) => {
  const [showIconSelector, setShowIconSelector] = React.useState(false);

  // Safety: Extract actual value if fieldValue is an object with {value, type}
  const actualValue = React.useMemo(() => {
    if (fieldValue && typeof fieldValue === 'object' && !Array.isArray(fieldValue)) {
      const obj = fieldValue as any;
      if ('value' in obj) {
        return obj.value;
      }
    }
    return fieldValue;
  }, [fieldValue]);

  const renderField = () => {
    switch (fieldType) {
      case 'items_list':
        // Render ItemsList for features, steps, values, etc.
        if (!sectionId) return <p className="text-red-500">Section ID required for items list</p>;

        // Determine item type from field key
        let itemType: ItemType = 'feature';
        if (fieldKey.includes('feature')) itemType = 'feature';
        else if (fieldKey.includes('step')) itemType = 'step';
        else if (fieldKey.includes('value')) itemType = 'value';
        else if (fieldKey.includes('faq')) itemType = 'faq';
        else if (fieldKey.includes('stat')) itemType = 'stat';
        else if (fieldKey.includes('contact')) itemType = 'contact_method';
        else if (fieldKey.includes('highlight')) itemType = 'highlight';

        return (
          <ItemsList
            sectionId={sectionId}
            itemType={itemType}
            language={language}
            onItemsChange={onItemsChange || (() => {})}
          />
        );

      case 'statistics':
        // Render StatisticsEditor
        if (!sectionId) return <p className="text-red-500">Section ID required for statistics</p>;

        return (
          <StatisticsEditor
            sectionId={sectionId}
            language={language}
            onItemsChange={onItemsChange || (() => {})}
          />
        );

      case 'icon_selector':
        // Render icon selector button
        const displayValue = typeof actualValue === 'string' || typeof actualValue === 'number' ? String(actualValue) : '';
        return (
          <div>
            <button
              onClick={() => setShowIconSelector(true)}
              className="px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 transition-colors"
            >
              Select Icon
            </button>
            {displayValue && <p className="mt-2 text-sm text-gray-600">Selected Icon ID: {displayValue}</p>}
            {showIconSelector && (
              <IconSelector
                selectedIconId={typeof actualValue === 'number' ? actualValue : (typeof actualValue === 'string' ? parseInt(actualValue) : null)}
                onSelect={(iconId) => {
                  onChange(iconId.toString());
                  setShowIconSelector(false);
                }}
                onClose={() => setShowIconSelector(false)}
              />
            )}
          </div>
        );

      case 'number':
        return (
          <input
            type="text"
            value={typeof actualValue === 'string' || typeof actualValue === 'number' ? String(actualValue) : ''}
            onChange={(e) => onChange(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="e.g., 10,000+ or 98%"
          />
        );

      case 'url':
        return (
          <input
            type="url"
            value={typeof actualValue === 'string' || typeof actualValue === 'number' ? String(actualValue) : ''}
            onChange={(e) => onChange(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder="https://example.com"
          />
        );

      case 'rich_text':
        // Basic rich text - for now just textarea with some hints
        return (
          <div className="space-y-2">
            <textarea
              value={typeof actualValue === 'string' || typeof actualValue === 'number' ? String(actualValue) : ''}
              onChange={(e) => onChange(e.target.value)}
              rows={6}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical"
              placeholder={`Enter ${label.toLowerCase()}`}
            />
            <p className="text-xs text-gray-500">
              Tip: Use **bold** for bold text, [link text](url) for links
            </p>
          </div>
        );

      case 'text':
        return (
          <input
            type="text"
            value={typeof actualValue === 'string' || typeof actualValue === 'number' ? String(actualValue) : ''}
            onChange={(e) => onChange(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
            placeholder={`Enter ${label.toLowerCase()}`}
          />
        );

      case 'textarea':
        return (
          <textarea
            value={typeof actualValue === 'string' || typeof actualValue === 'number' ? String(actualValue) : ''}
            onChange={(e) => onChange(e.target.value)}
            rows={5}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical"
            placeholder={`Enter ${label.toLowerCase()}`}
          />
        );

      case 'image':
        return (
          <ImageUploader
            currentImage={typeof actualValue === 'string' || typeof actualValue === 'number' ? String(actualValue) : ''}
            onImageUploaded={onChange}
            label={label}
          />
        );

      case 'color':
        const colorValue = typeof actualValue === 'string' || typeof actualValue === 'number' ? String(actualValue) : '#000000';
        return (
          <div className="flex items-center space-x-3">
            <input
              type="color"
              value={colorValue}
              onChange={(e) => onChange(e.target.value)}
              className="h-10 w-20 border border-gray-300 rounded cursor-pointer"
            />
            <input
              type="text"
              value={colorValue}
              onChange={(e) => onChange(e.target.value)}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              placeholder="#000000"
              pattern="^#[0-9A-Fa-f]{6}$"
            />
          </div>
        );

      case 'json':
        return (
          <div className="space-y-2">
            <textarea
              value={
                typeof actualValue === 'string'
                  ? actualValue
                  : JSON.stringify(actualValue, null, 2)
              }
              onChange={(e) => {
                try {
                  const parsed = JSON.parse(e.target.value);
                  onChange(parsed);
                } catch {
                  // Keep as string if invalid JSON
                  onChange(e.target.value);
                }
              }}
              rows={10}
              className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent font-mono text-sm resize-vertical"
              placeholder="Enter valid JSON"
            />
            <p className="text-xs text-gray-500">
              Enter valid JSON format. Use the JSON Array Editor below for easier editing.
            </p>
          </div>
        );

      default:
        // Safely convert any value to string
        const safeValue = (() => {
          if (typeof actualValue === 'string') return actualValue;
          if (typeof actualValue === 'number') return String(actualValue);
          if (typeof actualValue === 'boolean') return String(actualValue);
          if (actualValue === null || actualValue === undefined) return '';
          // For objects/arrays, stringify them
          try {
            return JSON.stringify(actualValue);
          } catch {
            return '';
          }
        })();

        return (
          <input
            type="text"
            value={safeValue}
            onChange={(e) => onChange(e.target.value)}
            className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
          />
        );
    }
  };

  return (
    <div className="space-y-2">
      <label className="block text-sm font-medium text-gray-700">
        {label}
        <span className="ml-2 text-xs text-gray-500">({fieldType})</span>
      </label>
      {renderField()}
    </div>
  );
};

export default FieldEditor;
